import { ArrowRight, ChefHat, Clock3, MessageCircle, MonitorCog, QrCode, ShieldCheck, UsersRound } from 'lucide-react'
import { Link } from 'react-router-dom'
import BrandMark from '../components/BrandMark'
import { useLamsa } from '../context/LamsaContext'

export default function LandingPage() {
  const { restaurant } = useLamsa()
  return (
    <div className="marketing-page">
      <header className="marketing-nav">
        <BrandMark />
        <nav>
          <a href="#experience">Experience</a>
          <a href="#operations">Operations</a>
          <a href="#studio">Studio</a>
        </nav>
        <Link className="button button-gold" to="/app/foh">Open Demo <ArrowRight size={16} /></Link>
      </header>

      <section className="hero-section">
        <div className="hero-copy">
          <span className="hero-kicker">THE LIVE RESTAURANT EXPERIENCE LAYER</span>
          <h1>One table.<br /><span>One live experience.</span></h1>
          <p>Lamsa Table connects guests, front of house, kitchen and management in real time—without replacing the POS a restaurant already trusts.</p>
          <div className="hero-actions">
            <Link className="button button-gold button-lg" to="/r/ava-xin-delight/t/12">Try Guest Demo <ArrowRight size={18} /></Link>
            <Link className="button button-outline button-lg" to="/app/foh">View Operations</Link>
          </div>
          <div className="hero-proof">
            <span><ShieldCheck size={17} /> POS friendly</span>
            <span><QrCode size={17} /> QR + NFC ready</span>
            <span><Clock3 size={17} /> Live elapsed timing</span>
          </div>
        </div>
        <div className="hero-product-stage" aria-label="Lamsa product preview">
          <div className="hero-dashboard-card">
            <div className="fake-window-bar"><i /><i /><i /><span>Front of House · Live Tables</span></div>
            <div className="fake-dashboard-grid">
              {[
                ['12', 'NEW ORDER', '00:38'], ['06', 'PREPARING', '12:22'], ['04', 'READY', '01:08'],
                ['07', 'BILL', '02:12'], ['09', 'ASSIST', '03:44'], ['02', 'BROWSING', '2 guests'],
              ].map(([n, status, time]) => <div className="fake-table" key={n}><strong>Table {n}</strong><span>{status}</span><small>{time}</small></div>)}
            </div>
          </div>
          <div className="hero-phone-card">
            <div className="phone-notch" />
            <span className="phone-brand">{restaurant.name}</span>
            <img src="https://picsum.photos/seed/lamsa-hero-food/700/520" alt="Demo menu item" />
            <strong>AVA Signature Burger</strong>
            <p>Smashed beef, cheddar & house sauce</p>
            <button>+ Add · BD 4.900</button>
          </div>
          <div className="hero-alert-card"><MessageCircle size={19} /><span><strong>Table 12</strong> “Can we have extra ketchup?”</span></div>
        </div>
      </section>

      <section className="marketing-feature-row" id="experience">
        <article><UsersRound /><h3>Shared Table Ordering</h3><p>Every guest browses individually while one shared table order updates live.</p></article>
        <article><ChefHat /><h3>Live Team Coordination</h3><p>Counter, kitchen and service teams see the same order status and elapsed time.</p></article>
        <article><MessageCircle /><h3>Guest Communication</h3><p>Table chat and structured service requests stay attached to the active session.</p></article>
        <article><MonitorCog /><h3>Client Studio</h3><p>Restaurants edit menu items, prices, text, imagery and branding without code.</p></article>
      </section>

      <section className="marketing-split" id="operations">
        <div className="split-copy">
          <span className="section-number">01 / OPERATIONS</span>
          <h2>Know what needs attention before it becomes a problem.</h2>
          <p>Orders, ready food, bills and assistance requests surface in one operational queue. Every actionable item carries an elapsed timer.</p>
          <Link to="/app/foh">Explore Front of House <ArrowRight size={17} /></Link>
        </div>
        <div className="ops-preview">
          <div className="ops-metric"><span>Active tables</span><strong>8</strong></div>
          <div className="ops-metric"><span>New orders</span><strong>3</strong></div>
          <div className="ops-metric warning"><span>Critical delays</span><strong>1</strong></div>
          <div className="ops-queue-row"><b>Table 12</b><span>New Order</span><em>00:38</em><button>View</button></div>
          <div className="ops-queue-row"><b>Table 04</b><span>Food Ready</span><em>01:08</em><button>Serve</button></div>
          <div className="ops-queue-row"><b>Table 07</b><span>Bill Request</span><em>02:12</em><button>Handle</button></div>
        </div>
      </section>

      <section className="marketing-dark-section" id="studio">
        <div>
          <span className="section-number">02 / STUDIO</span>
          <h2>Your restaurant should never look like someone else’s template.</h2>
          <p>Give each client a simple Studio for names, descriptions, prices, availability, imagery, brand colours and guest-facing content—with a live preview beside the editor.</p>
          <Link className="button button-gold" to="/app/studio">Open Studio Demo <ArrowRight size={16} /></Link>
        </div>
        <div className="studio-preview-card">
          <div className="studio-field"><label>Restaurant name</label><span>AVA XIN Delight</span></div>
          <div className="studio-field"><label>Signature item</label><span>AVA Signature Burger</span></div>
          <div className="studio-field"><label>Price</label><span>BD 4.900</span></div>
          <div className="studio-preview-phone"><img src="https://picsum.photos/seed/studio-preview/600/420" alt="Studio live preview" /><strong>AVA Signature Burger</strong><small>BD 4.900</small></div>
        </div>
      </section>

      <footer className="marketing-footer"><BrandMark /><p>Built for restaurants that want a better guest experience and a clearer operation.</p><span>lamsatable.xinavane.com · development pilot</span></footer>
    </div>
  )
}
